<script lang="ts">
import { ChevronLeft, ChevronRight } from 'lucide-svelte';
import { carousel, type EmblaCarouselType } from '$lib/actions/carousel.js';
import {
    formatBuilderImageSrcsetForAspectRatio,
    formatBuilderImageUrlForAspectRatio,
} from '$lib/utils/formatImageUrl.js';
import SectionHeader from '../../atoms/SectionHeader/SectionHeader.svelte';
import SectionText from '../../atoms/SectionText/SectionText.svelte';

interface Slide {
    image: {
        src?: string;
        alt?: string;
        altText?: string;
        [key: string]: unknown;
    };
    title?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    text?: string;
}

let { backgroundColour = '', txtColour = '', slides = [] as Slide[], hideBlock = false } = $props();

let currentSlide = $state(0);
let emblaApi: EmblaCarouselType | null = $state(null);
const hasMultipleSlides = $derived(Array.isArray(slides) && slides.length > 1);
const SLIDER_IMAGE_ASPECT_RATIO = 16 / 9;
const SLIDER_IMAGE_PARAMS = 'format=webp&quality=75';
const SLIDER_IMAGE_WIDTHS = [480, 640, 960, 1280, 1600] as const;
const SLIDER_IMAGE_FALLBACK_WIDTH = 1280;
const SLIDER_IMAGE_SIZES = '100vw';
</script>

{#if !hideBlock}
    <div
        class="feature-slider"
        style="--bg-colour: {backgroundColour}; --txt-colour: {txtColour};"
        use:carousel={{
            ariaLabel: 'Feature slides',
            slideCount: slides.length,
            onInit: (api) => { emblaApi = api; },
            onSelect: (index) => { currentSlide = index; },
        }}
    >
        <div class="feature-slider__viewport" data-carousel-viewport>
            <div class="feature-slider__slides">
                {#each slides as slide, index (index)}
                    <div class="feature-slider-slide" data-carousel-slide>
                        <img
                            src={formatBuilderImageUrlForAspectRatio(
                                slide.image?.src,
                                SLIDER_IMAGE_FALLBACK_WIDTH,
                                SLIDER_IMAGE_ASPECT_RATIO,
                                SLIDER_IMAGE_PARAMS,
                            )}
                            srcset={formatBuilderImageSrcsetForAspectRatio(
                                slide.image?.src,
                                SLIDER_IMAGE_WIDTHS,
                                SLIDER_IMAGE_ASPECT_RATIO,
                                SLIDER_IMAGE_PARAMS,
                            )}
                            sizes={SLIDER_IMAGE_SIZES}
                            alt={slide.image?.altText || slide.image?.alt || ''}
                            class="feature-slider-slide__image"
                            loading="lazy"
                            decoding="async"
                        />
                        {#if slide.title}
                            <SectionHeader headingLevel={slide.headingLevel} titleSize={slide.titleSize}
                                >{slide.title}</SectionHeader
                            >
                        {/if}
                        {#if slide.text}
                            <SectionText>{slide.text}</SectionText>
                        {/if}
                    </div>
                {/each}
            </div>
        </div>

        {#if hasMultipleSlides}
            <div class="carousel-nav" role="navigation" aria-label="Slide navigation">
                <button
                    class="carousel-arrow"
                    aria-label="Previous slide"
                    onclick={() => emblaApi?.scrollPrev()}
                >
                    <ChevronLeft size={16} />
                </button>
                <div class="carousel-dots">
                    {#each slides as slide, index (index)}
                        <button
                            class="carousel-dot"
                            class:carousel-dot--active={currentSlide === index}
                            aria-current={currentSlide === index ? 'step' : undefined}
                            aria-label="Go to slide {index + 1}: {slide.title ?? ''}"
                            onclick={() => emblaApi?.scrollTo(index)}
                        ></button>
                    {/each}
                </div>
                <button
                    class="carousel-arrow"
                    aria-label="Next slide"
                    onclick={() => emblaApi?.scrollNext()}
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        {/if}
    </div>
{/if}

<style lang="scss">
    @use "/src/scss/components/carousel" as carousel;

    .feature-slider {
        padding: var(--layout-pagegrid-margin);
        background-color: var(--bg-colour);
        color: var(--txt-colour);
        position: relative;
    }

    .feature-slider__viewport {
        overflow: hidden;
    }

    .feature-slider__slides {
        display: flex;
    }

    .feature-slider-slide {
        flex: 0 0 100%;
        min-width: 0;
        padding-bottom: var(--layout-pagegrid-margin);

        :global(.feature-slider-slide__image) {
            aspect-ratio: var(--layout-media-ar);
            margin-bottom: 20px;
            object-fit: cover;
        }

        :global(picture),
        :global(img) {
            width: 100%;
        }
    }

    .carousel-nav {
        @include carousel.nav;
    }

    .carousel-arrow {
        @include carousel.arrow-base;
        color: var(--txt-colour);
    }

    .carousel-dots {
        @include carousel.dots;
        --carousel-dot-colour: var(--colour-million-grey);
    }

    .carousel-dot {
        @include carousel.dot;
    }

    .carousel-dot--active {
        @include carousel.dot-active;
    }
</style>
